import { Logger, Cacher } from "@ng-mw/shared-react-components"
import { useEffect, useState } from "react"
import { CampaignDetailsAndProducts, HookState } from "../types/CampaignAdminTypes"
import { getSingleCampaignByTag } from "../helpers/getSingleCampaignByTag"
import { getProductsByCampaignId } from "../helpers/getProductsByCampaignId"
import { useSelector } from "react-redux"
import { RootState } from "@app/types"
import { UnwrapFromCache, WrapForCache } from "../helpers/campaignDetailsAndProductsCacheWrapper"

/**
 * React hook that resolves a campaign and its connected products
 * using a given campaignAdminTag. Handles data fetching, loading state, and errors.
 *
 * @param campaignAdminTag - Optional unique identifier for the campaign.
 * @returns An object containing:
 * - `state`: The current status of the fetch process ('idle' | 'loading' | 'resolved' | 'error').
 * - `data`: The combined campaign details and products, or `null` if not available.
 * - `errorMessage`: A string describing any error encountered, or an empty string if none.
 */
export const useCampaignAdminTagToFindSingleCampaignAndConnectedProducts = (
    campaignAdminTag: string,
) => {
    const [data, setData] = useState<CampaignDetailsAndProducts | null>(null)
    const [state, setState] = useState<HookState>("idle")
    const [errorMessage, setErrorMessage] = useState("")

    const storeId = useSelector((state: RootState) => state.user.data.store.gln) ?? ""

    useEffect(() => {
        const CACHE_KEY = "useCampaignAdminTagToFindSingleCampaignAndConnectedProducts"
        const CACHE_PREDICATE = {
            storeId,
            campaignAdminTag,
        }

        const _fetchContent = async (campaignAdminTag: string): Promise<void> => {
            setState("pending")
            setErrorMessage("")

            try {
                const cached = await Cacher.get(CACHE_KEY, CACHE_PREDICATE)

                if (cached !== null) {
                    setData(UnwrapFromCache(cached))
                    setState("resolved")
                } else {
                    // Step 1: Fetch campaign details using the provided tag.
                    const campaignDetails = await getSingleCampaignByTag(storeId, campaignAdminTag)

                    if (campaignDetails) {
                        // Step 2: Fetch products associated with the retrieved campaign.
                        const products = await getProductsByCampaignId(
                            storeId,
                            campaignDetails.campaignId,
                        )

                        // Step 3: Combine campaign details and products into a single data object.
                        const data: CampaignDetailsAndProducts = {
                            campaignDetails,
                            products,
                        }

                        // Step 4: Cache response.
                        Cacher.cache(CACHE_KEY, CACHE_PREDICATE, WrapForCache(data))

                        // Step 5: Resolve hook.
                        setData(data)
                    } else {
                        // Don't log errors just because there are no results.
                        setData(null)
                    }
                    setState("resolved")
                }
            } catch (error: unknown) {
                const errorMessage: string =
                    error instanceof Error ? error.message : "unknown error"

                Logger.error(
                    "modules/bundles/hooks/useCampaignAdminTagToFindSingleCampaignAndConnectedProducts",
                    error,
                    CACHE_PREDICATE,
                )

                Cacher.cache(CACHE_KEY, CACHE_PREDICATE, WrapForCache(null))

                setData(null)
                setErrorMessage(errorMessage)
                setState("rejected")
            }
        }

        _fetchContent(campaignAdminTag)
    }, [campaignAdminTag, storeId])

    return { state, data, errorMessage }
}
